import type { CourseData } from '../course/model.js';

/**
 * The store talks to the host through these two interfaces so
 * `packages/shared` keeps its zero-Office.js rule. `packages/host-bridge`
 * supplies the real implementations; tests supply in-memory ones.
 */
export interface SettingsBackend {
  /** Synchronous read of the in-memory settings bag. */
  read(key: string): unknown;
  /** Synchronous write into the in-memory bag; not durable until `save`. */
  write(key: string, value: unknown): void;
  remove(key: string): void;
  /** The `saveAsync` round trip. Rejects if the host refuses the write. */
  save(): Promise<void>;
  /**
   * Practical ceiling for the whole settings bag, in bytes, from SPIKE 2.
   * Measured rather than documented — there is no published limit.
   */
  readonly capacityBytes: number;
}

/**
 * Used when course data outgrows the settings ceiling: the payload moves to the
 * publishing service and only a pointer stays in the deck.
 */
export interface SidecarBackend {
  readonly available: boolean;
  put(courseId: string, payload: string): Promise<string>;
  get(pointer: string): Promise<string | undefined>;
}

export type StoreEvent =
  | { readonly kind: 'changed'; readonly data: CourseData }
  | { readonly kind: 'saved'; readonly bytes: number; readonly location: 'settings' | 'sidecar' }
  | {
      readonly kind: 'quota-warning';
      readonly bytes: number;
      readonly capacityBytes: number;
      readonly message: string;
    }
  | {
      readonly kind: 'clobber-detected';
      readonly message: string;
      readonly theirWriter: string;
      readonly ourWriter: string;
    }
  | { readonly kind: 'error'; readonly code: StoreErrorCode; readonly message: string };

export type Unsubscribe = () => void;

export type StoreErrorCode =
  | 'quota-exceeded'
  | 'corrupt-blob'
  | 'from-the-future'
  | 'sidecar-unavailable'
  | 'migration-failed'
  | 'save-failed';

export class StoreError extends Error {
  constructor(
    readonly code: StoreErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'StoreError';
  }
}

/** What actually sits under the settings key. */
export interface StoredEnvelope {
  readonly envelopeVersion: 1;
  /** Increments on every successful write; used to detect a co-authoring clobber. */
  readonly revision: number;
  /** Random per-session id, so we can tell our own writes from someone else's. */
  readonly writer: string;
  readonly writtenAt: string;
  readonly checksum: string;
  readonly location: 'settings' | 'sidecar';
  /** JSON string of `CourseData` when inline; absent for sidecar. */
  readonly payload?: string;
  /** Opaque handle understood by the sidecar backend. */
  readonly pointer?: string;
}
